import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TAX_DISCLOSURE, SUPPORT_EMAIL } from '../config';
import { formatMoney, formatDate } from '../lib/format';
import usePageMeta from '../hooks/usePageMeta';
import styles from './DonorPortal.module.css';

export default function DonorPortal() {
  usePageMeta('Your giving — Revive Worship');

  const [params] = useSearchParams();
  const initialToken = params.get('token');

  const [token, setToken] = useState(initialToken);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setLoadError('');
    fetch(`/.netlify/functions/get-donor-history?token=${encodeURIComponent(token)}`)
      .then((r) => (r.ok
        ? r.json()
        : r.json().catch(() => ({})).then((d) => Promise.reject(new Error(d.error || 'Link expired or invalid')))))
      .then(setData)
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const requestLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setRequesting(true);
    setRequestError('');
    try {
      const res = await fetch('/.netlify/functions/request-magic-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Could not send link');
      }
      setRequestSent(true);
    } catch (err) {
      setRequestError(err.message || 'Could not send link. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  const downloadPdf = (year) => {
    if (!token) return;
    const base = `/.netlify/functions/generate-receipt-pdf?token=${encodeURIComponent(token)}`;
    window.location.href = year ? `${base}&year=${year}` : base;
  };

  const manageRecurring = async () => {
    if (!token) return;
    setPortalLoading(true);
    try {
      const res = await fetch('/.netlify/functions/customer-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const d = await res.json();
      if (d.url) {
        window.location.href = d.url;
      } else {
        throw new Error('no portal url');
      }
    } catch {
      setPortalLoading(false);
      window.alert(`We could not open the management portal. Please email ${SUPPORT_EMAIL} and we will help.`);
    }
  };

  const restart = () => {
    setToken(null);
    setData(null);
    setLoadError('');
    setRequestSent(false);
    setEmail('');
  };

  const years = data?.charges
    ? Array.from(new Set(data.charges.map((c) => new Date(c.date).getUTCFullYear()))).sort((a, b) => b - a)
    : [];

  const RequestForm = () => {
    if (requestSent) {
      return (
        <div className={styles.sentBox}>
          <p>Check your email for a secure link. It's good for 24 hours.</p>
          <p className={styles.sentNote}>Didn't get it? Make sure you used the email you gave with, and check spam.</p>
        </div>
      );
    }
    return (
      <form className={styles.form} onSubmit={requestLink}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>email</label>
          <input
            className={styles.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>
        {requestError && <p className={styles.errorMsg}>{requestError}</p>}
        <button className={styles.primary} type="submit" disabled={requesting}>
          {requesting ? 'sending…' : 'email me my giving link'}
          <span aria-hidden="true">→</span>
        </button>
      </form>
    );
  };

  return (
    <section className={styles.page}>
      <div className={styles.shell}>
        {!token && (
          <>
            <span className={styles.label}>donor portal</span>
            <h1 className={styles.title}>Your giving <em>record.</em></h1>
            <p className={styles.body}>
              Enter the email you gave with. We'll send a secure link to view your full giving history and download a tax-deductible receipt.
            </p>
            <RequestForm />
          </>
        )}

        {token && loading && (
          <>
            <span className={styles.label}>loading</span>
            <h1 className={styles.title}>One moment…</h1>
            <p className={styles.body}>Loading your giving record.</p>
          </>
        )}

        {token && loadError && (
          <>
            <span className={styles.label}>link expired</span>
            <h1 className={styles.title}>Your link <em>expired.</em></h1>
            <p className={styles.body}>Magic links are valid for 24 hours. Request a fresh one below.</p>
            <RequestForm />
          </>
        )}

        {token && data && !loadError && (
          <>
            <span className={styles.label}>your giving</span>
            <h1 className={styles.title}>
              {data.donor?.name ? `${data.donor.name.split(' ')[0]},` : 'Friend,'} <em>thank you.</em>
            </h1>
            <p className={styles.body}>
              Your total to date: <em>{formatMoney(data.total, data.currency)}</em> across {data.charges.length} {data.charges.length === 1 ? 'gift' : 'gifts'}.
            </p>

            <div className={styles.actions}>
              <button type="button" className={styles.primary} onClick={() => downloadPdf(null)}>
                download tax receipt (pdf) <span aria-hidden="true">↓</span>
              </button>
              {years.length > 1 && years.map((y) => (
                <button key={y} type="button" className={styles.ghost} onClick={() => downloadPdf(y)}>
                  {y} statement
                </button>
              ))}
              {data.has_recurring && (
                <button type="button" className={styles.ghost} onClick={manageRecurring} disabled={portalLoading}>
                  {portalLoading ? 'opening…' : 'manage recurring gift'}
                </button>
              )}
            </div>

            {data.charges.length > 0 && (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>date</th>
                    <th>designation</th>
                    <th className={styles.amount}>amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.charges.slice().reverse().map((c) => (
                    <tr key={c.id}>
                      <td>{formatDate(c.date)}</td>
                      <td>{c.designation || 'General fund'}</td>
                      <td className={styles.amount}>{formatMoney(c.amount, c.currency)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="2">Total</td>
                    <td className={styles.amount}>{formatMoney(data.total, data.currency)}</td>
                  </tr>
                </tfoot>
              </table>
            )}

            <button type="button" className={styles.linkBtn} onClick={restart}>use a different email</button>
          </>
        )}

        <p className={styles.disclosure}>{TAX_DISCLOSURE}</p>
        <p className={styles.backRow}><Link to="/" className={styles.backLink}>← back to revive</Link></p>
      </div>
    </section>
  );
}
